import { useEffect, useRef } from "react";
import * as THREE from "three";
import { OrbitControls } from "three/examples/jsm/controls/OrbitControls.js";

interface ThreePreviewProps {
  geometry: THREE.BufferGeometry | null;
  mode: "solid" | "backlit";
}

export default function ThreePreview({
  geometry,
  mode,
}: ThreePreviewProps) {
  const hostRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    const host = hostRef.current;
    if (!host) return;

    const scene = new THREE.Scene();
    scene.background = new THREE.Color(mode === "backlit" ? 0x090b0d : 0x171b1e);

    const camera = new THREE.PerspectiveCamera(38, 1, 0.1, 10_000);
    const renderer = new THREE.WebGLRenderer({
      antialias: true,
      alpha: false,
      preserveDrawingBuffer: true,
    });
    renderer.setPixelRatio(Math.min(window.devicePixelRatio, 2));
    renderer.outputColorSpace = THREE.SRGBColorSpace;
    renderer.shadowMap.enabled = true;
    host.appendChild(renderer.domElement);

    const controls = new OrbitControls(camera, renderer.domElement);
    controls.enableDamping = true;
    controls.dampingFactor = 0.07;
    controls.enablePan = true;

    let mesh: THREE.Mesh | null = null;
    const disposableMaterials: THREE.Material[] = [];

    if (geometry) {
      const solidMaterial = new THREE.MeshStandardMaterial({
        color: 0xf1eee7,
        roughness: 0.72,
        metalness: 0,
        side: THREE.DoubleSide,
      });

      const backlitFrontMaterial = new THREE.MeshBasicMaterial({
        vertexColors: true,
        side: THREE.DoubleSide,
      });

      const backlitShellMaterial = new THREE.MeshBasicMaterial({
        color: 0x292d30,
        side: THREE.DoubleSide,
      });

      disposableMaterials.push(
        solidMaterial,
        backlitFrontMaterial,
        backlitShellMaterial,
      );

      mesh = new THREE.Mesh(
        geometry,
        mode === "backlit"
          ? [backlitFrontMaterial, backlitShellMaterial]
          : [solidMaterial, solidMaterial],
      );
      mesh.castShadow = true;
      mesh.receiveShadow = true;
      scene.add(mesh);

      const bounds = geometry.boundingBox?.clone() ?? new THREE.Box3().setFromObject(mesh);
      const center = bounds.getCenter(new THREE.Vector3());
      const size = bounds.getSize(new THREE.Vector3());
      const maximumDimension = Math.max(size.x, size.y, size.z, 20);
      const fieldOfViewRadians = THREE.MathUtils.degToRad(camera.fov);
      const distance =
        (maximumDimension / (2 * Math.tan(fieldOfViewRadians / 2))) * 1.45;

      controls.target.copy(center);
      camera.position.set(
        center.x + distance * 0.75,
        center.y + distance * 0.15,
        center.z + distance,
      );
      camera.near = Math.max(0.1, distance / 500);
      camera.far = distance * 20;
      camera.updateProjectionMatrix();
      controls.update();

      if (mode === "solid") {
        const ambient = new THREE.HemisphereLight(0xffffff, 0x26313b, 2.1);
        scene.add(ambient);

        const key = new THREE.DirectionalLight(0xffffff, 3.5);
        key.position.set(distance, distance, distance * 1.4);
        key.castShadow = true;
        scene.add(key);

        const fill = new THREE.DirectionalLight(0xb9d6ff, 1.6);
        fill.position.set(-distance, distance * 0.2, distance * 0.4);
        scene.add(fill);

        const grid = new THREE.GridHelper(
          maximumDimension * 2.4,
          16,
          0x46515a,
          0x2a3035,
        );
        grid.position.y = bounds.min.y - Math.max(1, maximumDimension * 0.015);
        scene.add(grid);
      }
    } else {
      camera.position.set(0, 0, 5);
    }

    const resize = () => {
      const width = Math.max(1, host.clientWidth);
      const height = Math.max(1, host.clientHeight);
      renderer.setSize(width, height, false);
      camera.aspect = width / height;
      camera.updateProjectionMatrix();
    };

    const resizeObserver = new ResizeObserver(resize);
    resizeObserver.observe(host);
    resize();

    let animationFrame = 0;
    const animate = () => {
      animationFrame = requestAnimationFrame(animate);
      controls.update();
      renderer.render(scene, camera);
    };
    animate();

    return () => {
      cancelAnimationFrame(animationFrame);
      resizeObserver.disconnect();
      controls.dispose();
      disposableMaterials.forEach((material) => material.dispose());
      renderer.dispose();
      renderer.domElement.remove();
      scene.clear();
    };
  }, [geometry, mode]);

  return (
    <div className="preview-canvas">
      <div className="three-host" ref={hostRef} />
      {!geometry && (
        <div className="preview-placeholder">
          <div className="placeholder-mark">L</div>
          <strong>Your 3D preview will appear here</strong>
          <span>Upload a photograph, adjust the dimensions, then generate.</span>
        </div>
      )}
    </div>
  );
}
